import { useRef, type ReactNode } from 'react';
import { AlertTriangle } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../ui/alert-dialog';

type ConfirmDialogSize = 'default' | 'sm';

interface ConfirmDialogBaseProps {
  title: string;
  description?: ReactNode;
  confirmLabel: string;
  onConfirm: () => void;
  /**
   * Rich body rendered below the header in a `<div>` (not the description
   * `<p>`), so it may contain block/interactive content — lists, entity chips
   * with popovers, etc. When only `body` is given, an accessible description is
   * derived from the title.
   */
  body?: ReactNode;
  /** Button variant for the confirm action (default: "destructive") */
  variant?: 'default' | 'destructive' | 'outline' | 'secondary' | 'ghost' | 'link';
  cancelLabel?: string;
  disabled?: boolean;
  size?: ConfirmDialogSize;
  icon?: ReactNode;
}

interface ControlledConfirmDialogProps extends ConfirmDialogBaseProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  trigger?: never;
}

interface TriggerConfirmDialogProps extends ConfirmDialogBaseProps {
  trigger: ReactNode;
  open?: never;
  onOpenChange?: never;
}

export type ConfirmDialogProps = ControlledConfirmDialogProps | TriggerConfirmDialogProps;

export function ConfirmDialog({
  title,
  description,
  body,
  confirmLabel,
  onConfirm,
  variant = 'destructive',
  cancelLabel = 'Cancel',
  disabled,
  size = 'default',
  icon = <AlertTriangle className="h-5 w-5 text-destructive" />,
  ...modeProps
}: ConfirmDialogProps) {
  const isControlled = 'open' in modeProps && modeProps.open !== undefined;
  const buttonSize = size === 'sm' ? 'sm' : 'default';
  const actionRef = useRef<HTMLButtonElement>(null);

  return (
    <AlertDialog
      open={isControlled ? modeProps.open : undefined}
      onOpenChange={isControlled ? modeProps.onOpenChange : undefined}
    >
      {!isControlled && 'trigger' in modeProps && (
        <AlertDialogTrigger asChild>{modeProps.trigger}</AlertDialogTrigger>
      )}
      <AlertDialogContent
        size={size}
        onOpenAutoFocus={(event) => {
          event.preventDefault();
          actionRef.current?.focus();
        }}
      >
        <AlertDialogHeader>
          <AlertDialogTitle className={icon ? 'flex items-center gap-2' : undefined}>
            {icon}
            {title}
          </AlertDialogTitle>
          {/* Keep an accessible description even when the visible detail lives
              in `body`: a hidden generic line so screen readers still get a
              description (Radix warns without one) without duplicating the
              title text. */}
          <AlertDialogDescription className={description ? undefined : 'sr-only'}>
            {description ?? 'Review the details below before confirming.'}
          </AlertDialogDescription>
        </AlertDialogHeader>
        {body ? <div className="text-sm">{body}</div> : null}
        <AlertDialogFooter>
          <AlertDialogCancel size={buttonSize}>{cancelLabel}</AlertDialogCancel>
          <AlertDialogAction
            ref={actionRef}
            variant={variant}
            size={buttonSize}
            onClick={onConfirm}
            disabled={disabled}
          >
            {confirmLabel}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
